/**
 * audioManager · v2.7.65+E219
 * ───────────────────────────
 * Central play-time governor + master stop for ALL admin-console system audio.
 *
 *  • Standard alerts / system sounds hard-stop after 30s of playback.
 *  • Panic / SOS alerts hard-stop after 45s of playback.
 *  • A clip shorter than its limit ends naturally (the cap only cuts off audio
 *    that would otherwise run past the limit — including looping alarms).
 *  • Every actively-playing element is tracked so a global indicator can show
 *    "audio is playing" and a single stopAll() silences EVERYTHING (standard
 *    chimes, booking klaxon, panic/SOS loop, Ashley TTS, the test panel…).
 *
 * This module ONLY governs how long audio may play and provides a master stop.
 * It does not change which sounds play, their volumes, or any alert logic.
 */

const STANDARD_LIMIT_MS = 30000; // 30s — standard alerts + system sounds
const BOOKING_LIMIT_MS = 60000;  // 60s — booking klaxons (rider + dispatch loops)
const PANIC_LIMIT_MS = 45000;    // 45s — panic / SOS only

// id -> { el, timer, onEnded, onStop }
const active = new Map();
const listeners = new Set();
let _seq = 0;

function _emit() {
  const playing = active.size > 0;
  listeners.forEach((fn) => {
    try { fn(playing, active.size); } catch { /* noop */ }
  });
}

/** Subscribe to play-state changes. Fires immediately with the current state. */
export function subscribe(fn) {
  listeners.add(fn);
  try { fn(active.size > 0, active.size); } catch { /* noop */ }
  return () => listeners.delete(fn);
}

export function isPlaying() { return active.size > 0; }
export function activeCount() { return active.size; }

function _release(id, hardStop) {
  const rec = active.get(id);
  if (!rec) return;
  active.delete(id);
  try { clearTimeout(rec.timer); } catch { /* noop */ }
  try { rec.el.removeEventListener("ended", rec.onEnded); } catch { /* noop */ }
  if (hardStop) {
    try { rec.el.pause(); } catch { /* noop */ }
    try { rec.el.currentTime = 0; } catch { /* noop */ }
    try { rec.onStop && rec.onStop(); } catch { /* noop */ }
  }
  _emit();
}

/**
 * Put an already-started HTMLAudioElement under governance.
 * @param {HTMLAudioElement} el
 * @param {{ kind?: "standard"|"booking"|"panic", onStop?: () => void }} opts
 * @returns {() => void} a stopper for this element.
 */
export function track(el, opts = {}) {
  if (!el || typeof window === "undefined") return () => {};
  // A reused (persistent) element restarting on a fresh alert → drop the old
  // registration (without stopping it) and re-arm a clean cap timer.
  for (const [id, rec] of active) {
    if (rec.el === el) { _release(id, false); break; }
  }
  const kind = opts.kind === "panic" ? "panic" : opts.kind === "booking" ? "booking" : "standard";
  const limit = kind === "panic" ? PANIC_LIMIT_MS : kind === "booking" ? BOOKING_LIMIT_MS : STANDARD_LIMIT_MS;
  const id = ++_seq;
  const onEnded = () => _release(id, false);            // natural end → just untrack
  const timer = setTimeout(() => _release(id, true), limit); // overrun → hard stop
  try { el.addEventListener("ended", onEnded); } catch { /* noop */ }
  active.set(id, { el, timer, onEnded, onStop: opts.onStop });
  _emit();
  return () => _release(id, true);
}

/** Untrack a specific element WITHOUT hard-stopping it (caller already paused it). */
export function untrackEl(el) {
  if (!el) return;
  for (const [id, rec] of active) {
    if (rec.el === el) _release(id, false);
  }
}

/** Master stop — silence + untrack EVERY governed audio element. */
export function stopAll() {
  Array.from(active.keys()).forEach((id) => _release(id, true));
}

/**
 * Track an element only once its playback has actually begun, so autoplay-
 * blocked elements never light up the indicator. Safe to pass a play() promise.
 */
export function trackWhenPlaying(el, playPromise, opts = {}) {
  if (!el) return;
  if (playPromise && typeof playPromise.then === "function") {
    playPromise.then(() => track(el, opts)).catch(() => { /* blocked → don't track */ });
  } else {
    track(el, opts);
  }
}
